import { Inject, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';

import { BehaviorSubject, EMPTY, interval, Subject } from 'rxjs';
import { auditTime, debounceTime, filter, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { LOCATION_TOKEN, UPDATE_SERVICE_AUTO_DELAY } from '@app/injection-tokens';
import { SummitNotificationService } from '@summit/services';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  protected notificationService: SummitNotificationService;
  protected disposing$ = new Subject();
  updateAvailable = new BehaviorSubject<boolean>(false);
  updateAvailableDebounced = false;

  constructor(
    private swUpdate: SwUpdate,
    private router: Router,
    @Inject(LOCATION_TOKEN) private locationObject: Location,
    @Inject(UPDATE_SERVICE_AUTO_DELAY) private DelayBeforeReload: number
  ) {
    this.notificationService = inject(SummitNotificationService);
    //console.log(`[UpdateService] constructor`, this.swUpdate.isEnabled);
    if (!this.swUpdate.isEnabled) {
      console.log('[UpdateService] Service Worker Update Service not enabled');
    } else {
      console.log('[UpdateService]Service Worker Update Service is enabled');

      // Handle version updates and installation failures
      this.swUpdate.versionUpdates.pipe(takeUntil(this.disposing$)).subscribe(async (evt) => {
        //console.log(`[UpdateService] versionUpdates emitted an event ${evt.type}`);
        switch (evt.type) {
          case 'VERSION_DETECTED':
            console.log('[UpdateService] New version detected, downloading...');
            break;

          case 'VERSION_READY':
            // Handle successful version update
            // console.log('[UpdateService] Update available event:', {
            //   type: 'UPDATE_AVAILABLE',
            //   current: evt.currentVersion,
            //   available: evt.latestVersion
            // });
            console.log('[UpdateService] New version detected via versionUpdates');
            this.updateAvailable.next(true);
            this.notificationService.triggerToast('Update Available', null, 'info', null, null, null, true);
            break;

          case 'VERSION_INSTALLATION_FAILED':
            // Handle installation failure - treat like VERSION_READY but with different message
            console.error(`[UpdateService] Version installation failed: ${evt.error}`);
            // console.error('[UpdateService] Failed version hash:', evt.version.hash);
            // console.log('[UpdateService] Will recover on user action (navigation or button click)');

            // Set update available to trigger the same user-driven recovery flow
            this.updateAvailable.next(true);

            // Show same toast as VERSION_READY - cache clearing in update() handles recovery
            this.notificationService.triggerToast('Update Available', null, 'info', null, null, null, true);
            break;

          case 'NO_NEW_VERSION_DETECTED':
            //console.log('[UpdateService] No new version available');
            break;

          case 'VERSION_FAILED':
            // Undocumented event that occurs when service worker detects version inconsistencies
            // Often happens during deployment or when cache/version checks conflict
            // console.warn('[UpdateService] Version check failed (likely transient):', evt);
            // console.warn(
            //   '[UpdateService] This usually resolves on next check or is handled by VERSION_INSTALLATION_FAILED recovery'
            // );
            break;

          default:
            console.warn(`[UpdateService] Unhandled version event type: ${(evt as any).type}`);
            break;
        }
      });

      // Handle unrecoverable state - service worker is broken
      this.swUpdate.unrecoverable.pipe(takeUntil(this.disposing$)).subscribe((evt) => {
        console.error('[UpdateService] Service worker in unrecoverable state:', evt.reason);

        // Clear everything and reload silently
        setTimeout(async () => {
          await this.clearServiceWorkerCaches();
          this.locationObject.reload();
        }, 1000);
      });

      // Periodically check for updates every 1 hour
      interval(60 * 60 * 1000)
        .pipe(
          startWith(0), // Check immediately on startup
          switchMap(() => this.swUpdate.checkForUpdate()),
          takeUntil(this.disposing$)
        )
        .subscribe({
          next: (updateFound) => {
            //console.log('[UpdateService] Periodic check completed, updateFound:', updateFound);
            if (updateFound) {
              console.log('[UpdateService] New version detected via periodic check');
              this.updateAvailable.next(true);
              this.notificationService.triggerToast('Update Available', null, 'info', null, null, null, true);
            }
          },
          error: (err) => {
            console.error('[UpdateService] Error checking for updates:', err);
          }
        });
      this.updateAvailable
        .pipe(
          auditTime(16),
          debounceTime(this.DelayBeforeReload),
          filter((status) => status == true),
          takeUntil(this.disposing$)
        )
        .subscribe((debouncedUpdate) => {
          // console.log('[UpdateService] Update debounced, setting updateAvailableDebounced =', debouncedUpdate);
          this.updateAvailableDebounced = debouncedUpdate;
        });
      this.router.events
        .pipe(
          //NavigationStart reloaded you on the page you were leaving
          filter((event) => event instanceof NavigationEnd),
          takeUntil(this.disposing$)
        )
        .subscribe(() => {
          // console.log(
          //   '[UpdateService] NavigationEnd detected, updateAvailableDebounced =',
          //   this.updateAvailableDebounced
          // );
          if (this.updateAvailableDebounced) {
            console.log('[UpdateService] Triggering update due to navigation');
            this.update();
          }
        });
    }
  }

  /**
   * Clears all service worker caches to recover from corrupted state
   */
  private async clearServiceWorkerCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        console.log('[UpdateService] Clearing caches:', cacheNames);
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        console.log('[UpdateService] All caches cleared');
      }

      // Attempt to unregister the service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
        console.log('[UpdateService] Service worker unregistered');
      }
    } catch (error) {
      console.error('[UpdateService] Error clearing caches:', error);
      throw error;
    }
  }

  async update() {
    console.trace('[UpdateService] update() called - Call stack:');
    await this.notificationService.showProgressSpinner(true, 'Updating');

    // Only do service worker operations if enabled
    if (this.swUpdate.isEnabled) {
      // Clear caches before updating to ensure clean recovery from any installation issues
      try {
        console.log('[UpdateService] Clearing caches before update...');
        await this.clearServiceWorkerCaches();
      } catch (err) {
        console.error('[UpdateService] Error clearing caches (continuing anyway):', err);
      }

      // Activate the new service worker version before reloading
      try {
        const activated = await this.swUpdate.activateUpdate();
        console.log('[UpdateService] New version activated:', activated);
      } catch (err) {
        console.error('[UpdateService] Error activating update:', err);
      }
    }

    // console.log(`Cleaning up`);
    setTimeout(() => {
      this.cleanup();
      this.locationObject.reload();
    }, 1000);
  }

  cleanup() {
    this.disposing$.next(EMPTY);
    this.disposing$.complete();
  }

  OnDestroy() {
    this.cleanup();
  }

  logStart() {
    // console.log(`[UpdateService] started`);
  }
}
